import "./index.css";
import { Waveform } from "./recorder/Waveform";
import { DurationDisplay } from "./recorder/DurationDisplay";
import { MicIcon } from "./recorder/MicIcon";
import { useRecorder } from "./recorder/useRecorder";
import { AudioQualityDisplay } from "./recorder/AudioQualityDisplay";
import { AudioConverter } from "./recorder/AudioConverter";
import { useTranscription } from "./recorder/useTranscription";

const STATE_LABELS = {
  ready: "Ready to record",
  recording: "Recording",
  paused: "Paused",
  stopped: "Stopped",
} as const;

export function App() {
  const { state, analyser, getElapsedMs, recordingFile, audioQuality, start, pause, resume, stop } = useRecorder();
  const { state: transcription, start: transcribe } = useTranscription();

  const handleMicClick = () => {
    if (state === "ready" || state === "stopped") {
      start();
    } else if (state === "recording") {
      pause();
    } else if (state === "paused") {
      resume();
    }
  };

  const canRecord = state === "ready" || state === "stopped" || state === "paused";
  const canPause = state === "recording";
  const canStop = state === "recording" || state === "paused";

  return (
    <div className="min-h-screen bg-[#0d1221] flex items-center justify-center">
      <div className="bg-[#161e32] rounded-[1.75rem] px-10 py-8 w-[500px] shadow-2xl">

        {/* Header */}
        <div className="flex justify-between items-start mb-5">
          <div>
            <p className="text-[#3dd8c0] text-[9px] tracking-[0.18em] uppercase font-semibold">
              Session State
            </p>
            <p className="text-white text-[1.05rem] font-medium mt-[5px]">
              {STATE_LABELS[state]}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[#3dd8c0] text-[9px] tracking-[0.18em] uppercase font-semibold">
              Quality
            </p>
            <AudioQualityDisplay quality={audioQuality} />
          </div>
        </div>

        {/* Waveform */}
        <div className="mb-8">
          <Waveform analyser={analyser} />
        </div>

        {/* Timer */}
        <div className="mb-8">
          <DurationDisplay running={state === "recording"} getElapsedMs={getElapsedMs} />
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-14">
          <button
            onClick={canPause ? pause : undefined}
            disabled={!canPause}
            title="Pause"
            className={`flex items-center justify-center w-9 h-9 transition-opacity ${
              canPause ? "opacity-80 hover:opacity-100 cursor-pointer" : "opacity-25 cursor-not-allowed"
            }`}
            aria-label="Pause"
          >
            <svg viewBox="0 0 24 24" fill="white" width="22" height="22">
              <rect x="5" y="3" width="4.5" height="18" rx="1.5" />
              <rect x="14.5" y="3" width="4.5" height="18" rx="1.5" />
            </svg>
          </button>

          <button
            onClick={canRecord ? handleMicClick : undefined}
            disabled={!canRecord}
            className={`w-[60px] h-[60px] rounded-full flex items-center justify-center shadow-lg transition-colors ${
              canRecord
                ? "bg-[#3dd8c0] hover:bg-[#35c4ae] active:bg-[#2eaf9b] shadow-[#3dd8c0]/20 cursor-pointer"
                : "bg-[#3dd8c0]/40 shadow-[#3dd8c0]/10 cursor-not-allowed"
            }`}
            title={state === "paused" ? "Resume recording" : "Start recording"}
            aria-label={state === "paused" ? "Resume recording" : "Start recording"}
          >
            <MicIcon />
          </button>

          <button
            onClick={canStop ? stop : undefined}
            disabled={!canStop}
            className={`flex items-center justify-center w-9 h-9 transition-opacity ${
              canStop ? "opacity-80 hover:opacity-100 cursor-pointer" : "opacity-25 cursor-not-allowed"
            }`}
            title="Stop"
            aria-label="Stop"
          >
            <svg viewBox="0 0 24 24" fill="white" width="22" height="22">
              <rect x="4" y="4" width="16" height="16" rx="2.5" />
            </svg>
          </button>
        </div>

        <AudioConverter file={recordingFile} />

        {recordingFile && transcription.status === "idle" && (
          <div className="mt-4 flex justify-center">
            <button
              onClick={transcribe}
              className="px-5 py-2 rounded-full border border-[#3dd8c0]/40 text-[#3dd8c0] text-sm hover:bg-[#3dd8c0]/10 transition-colors"
            >
              Transcribe
            </button>
          </div>
        )}

        {(transcription.status === "transcribing" || transcription.status === "done") && (
          <p className="mt-4 text-white/70 text-sm leading-relaxed">
            {transcription.text}
            {transcription.status === "transcribing" && <span className="animate-pulse">▋</span>}
          </p>
        )}

        {transcription.status === "error" && (
          <p className="mt-4 text-red-400/80 text-sm">{transcription.message}</p>
        )}

      </div>
    </div>
  );
}

export default App;
